// designer_components/NewProcessModal.js
import React from 'react';
import { IoClose } from 'react-icons/io5';
import PropTypes from 'prop-types';

const NewProcessModal = ({
  isOpen,
  onClose,
  onSubmit,
  formData,
  setFormData,
  errors,
  setErrors,
}) => {
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.form_phrase) newErrors.form_phrase = 'Form phrase is required';
    if (!formData.convo_name) newErrors.convo_name = 'Conversation name is required';
    if (!formData.form_type) newErrors.form_type = 'Form type is required';
    if (!formData.convo_description) newErrors.convo_description = 'Description is required';
    if (!formData.max_allowed_attempts) newErrors.max_allowed_attempts = 'Max attempts is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    } else {
      setErrors(prev => ({
        ...prev,
        general: 'Please fill in all required fields correctly.',
      }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-md w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-6 pb-0">
          <h2 className="text-2xl font-semibold">Create New Process</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 focus:outline-none"
            aria-label="Close modal"
          >
            <IoClose size={24} />
          </button>
        </div>

        <div className="overflow-y-auto p-6 flex-1">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Form Phrase
                {errors.form_phrase && <span className="text-red-500 ml-1">*</span>}
              </label>
              <input
                type="text"
                name="form_phrase"
                value={formData.form_phrase}
                onChange={handleInputChange}
                placeholder="E.g. mobile app opinion survey"
                className={`w-full p-2 border rounded-lg ${
                  errors.form_phrase ? 'border-red-500' : 'border-gray-300'
                } focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              />
              {errors.form_phrase && (
                <p className="text-red-500 text-sm mt-1">{errors.form_phrase}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Conversation Name
                {errors.convo_name && <span className="text-red-500 ml-1">*</span>}
              </label>
              <input
                type="text"
                name="convo_name"
                value={formData.convo_name}
                onChange={handleInputChange}
                placeholder="E.g. Customer Opinion on our Mobile App"
                className={`w-full p-2 border rounded-lg ${
                  errors.convo_name ? 'border-red-500' : 'border-gray-300'
                } focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              />
              {errors.convo_name && (
                <p className="text-red-500 text-sm mt-1">{errors.convo_name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
                {errors.convo_description && <span className="text-red-500 ml-1">*</span>}
              </label>
              <textarea
                name="convo_description"
                value={formData.convo_description}
                onChange={handleInputChange}
                placeholder="E.g. This is a survey to our customer..."
                rows={3}
                className={`w-full p-2 border rounded-lg ${
                  errors.convo_description ? 'border-red-500' : 'border-gray-300'
                } focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              />
              {errors.convo_description && (
                <p className="text-red-500 text-sm mt-1">{errors.convo_description}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Allowed Attempts
                  {errors.max_allowed_attempts && <span className="text-red-500 ml-1">*</span>}
                </label>
                <input
                  type="number"
                  name="max_allowed_attempts"
                  value={formData.max_allowed_attempts}
                  onChange={handleInputChange}
                  placeholder="E.g. 1"
                  className={`w-full p-2 border rounded-lg ${
                    errors.max_allowed_attempts ? 'border-red-500' : 'border-gray-300'
                  } focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                />
                {errors.max_allowed_attempts && (
                  <p className="text-red-500 text-sm mt-1">{errors.max_allowed_attempts}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Retry After Days
                </label>
                <input
                  type="number"
                  name="allows_retry_after_days"
                  value={formData.allows_retry_after_days}
                  onChange={handleInputChange}
                  placeholder="E.g. 9999"
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Process Type
                {errors.form_type && <span className="text-red-500 ml-1">*</span>}
              </label>
              <select
                name="form_type"
                value={formData.form_type}
                onChange={handleInputChange}
                className={`w-full p-2 border rounded-lg ${
                  errors.form_type ? 'border-red-500' : 'border-gray-300'
                } focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              >
                <option value="">Select Form Type</option>
                <option value="Survey">Survey</option>
                <option value="Questionnaire">Questionnaire</option>
                <option value="Feedback Form">Feedback Form</option>
                <option value="Support Questions">Support Questions</option>
                <option value="Verification">Verification</option>
              </select>
              {errors.form_type && (
                <p className="text-red-500 text-sm mt-1">{errors.form_type}</p>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="informs_sender_if_maxed"
                  name="informs_sender_if_maxed"
                  checked={formData.informs_sender_if_maxed}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="informs_sender_if_maxed" className="ml-2 text-sm text-gray-700">
                  Inform Sender When Max Attempts Reached
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="stems_logic_in_num"
                  name="stems_logic_in_num"
                  checked={formData.stems_logic_in_num}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="stems_logic_in_num" className="ml-2 text-sm text-gray-700">
                  Stems Logic in Numbers
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="skip_consent_prompt"
                  name="skip_consent_prompt"
                  checked={formData.skip_consent_prompt}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="skip_consent_prompt" className="ml-2 text-sm text-gray-700">
                  Skip Consent Prompt
                </label>
              </div>
            </div>

            {errors.general && (
              <div className="p-2 bg-red-50 rounded-lg">
                <p className="text-red-500 text-sm">{errors.general}</p>
              </div>
            )}
          </form>
        </div>

        <div className="p-6 border-t border-gray-200">
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Create Process
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

NewProcessModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  formData: PropTypes.shape({
    form_phrase: PropTypes.string,
    convo_name: PropTypes.string,
    convo_description: PropTypes.string,
    form_type: PropTypes.string,
    max_allowed_attempts: PropTypes.number,
    informs_sender_if_maxed: PropTypes.bool,
    allows_retry_after_days: PropTypes.number,
    stems_logic_in_num: PropTypes.bool,
    skip_consent_prompt: PropTypes.bool,
  }).isRequired,
  setFormData: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  setErrors: PropTypes.func.isRequired,
};

export default NewProcessModal;